import { QuotaNotice } from "@/components/billing/quota-notice";
import { Suspense, lazy, useMemo, useState } from "react";
import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "motion/react";
import {
  Atom,
  ArrowRight,
  CalendarCheck,
  ChevronRight,
  ClipboardList,
  Crown,
  FlaskConical,
  GraduationCap,
  Layers,
  Network,
  TrendingUp,
  Users,
  Wrench,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { BrandWordmark } from "@/components/app/brand-wordmark";
import FeatureShowcaseBanner from "@/components/app/feature-showcase-banner";
import { useEntitlement } from "@/hooks/use-entitlement";
import { useChapters } from "@/hooks/use-chapters";
import { buildDailyPlan } from "@/lib/lecture-plan";
import { completeQuestionPractice, fetchQuestionChapters } from "@/lib/questions.functions";
import { effectiveTarget, taskState, type QuestionChapter } from "@/lib/question-plan";
import { notifySaved, trackSave } from "@/lib/save-feedback";
import { cn } from "@/lib/utils";
import { seo, SITE_URL } from "@/lib/seo";
import { ProfileHeaderButton } from "@/components/app/profile-header-button";
import { ShareButton } from "@/components/app/share-button";
import { homepageContent, homepageSEO } from "@/lib/homepage-content";
import { usePremium, useUpgrade } from "@/components/billing/premium-lock";
import { SubscriptionBanner } from "@/components/SubscriptionBanner";

// Overlays load only when opened, keeping them out of the first-paint bundle.
const ChapterDetailSheet = lazy(() =>
  import("@/components/app/chapter-detail-sheet").then((m) => ({ default: m.ChapterDetailSheet })),
);
const SubscriptionModal = lazy(() =>
  import("@/components/SubscriptionModal").then((m) => ({ default: m.SubscriptionModal })),
);

export const Route = createFileRoute("/")({
  head: () => ({
    ...seo({
      title: homepageSEO.title,
      description: homepageSEO.description,
      path: "/",
    }),
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": `${SITE_URL}/#organization`,
              name: "The Quantum Minds",
              url: SITE_URL,
              logo: `${SITE_URL}/icon-192.png`,
            },
            {
              "@type": "WebSite",
              "@id": `${SITE_URL}/#website`,
              url: SITE_URL,
              name: "The Quantum Minds",
              publisher: { "@id": `${SITE_URL}/#organization` },
              description: homepageSEO.description,
            },
            {
              "@type": "WebPage",
              "@id": `${SITE_URL}/#webpage`,
              url: `${SITE_URL}/`,
              name: homepageSEO.title,
              description: homepageSEO.description,
              isPartOf: { "@id": `${SITE_URL}/#website` },
            },
            {
              "@type": "ItemList",
              "@id": `${SITE_URL}/#exam-trackers`,
              name: "Exam preparation trackers",
              itemListElement: [
                { name: "JEE Tracker", url: `${SITE_URL}/tracker/jee-main` },
                { name: "NEET Tracker", url: `${SITE_URL}/tracker/neet` },
                { name: "CBSE Tracker", url: `${SITE_URL}/tracker/cbse` },
                { name: "KCET Tracker", url: `${SITE_URL}/tracker/kcet` },
                { name: "MHT CET Tracker", url: `${SITE_URL}/tracker/mht-cet` },
              ].map((item, index) => ({
                "@type": "ListItem",
                position: index + 1,
                name: item.name,
                url: item.url,
              })),
            },
            {
              "@type": "WebApplication",
              "@id": `${SITE_URL}/#app`,
              name: "The Quantum Minds",
              url: SITE_URL,
              applicationCategory: "EducationalApplication",
              operatingSystem: "Web, Android, iOS",
              description:
                "A JEE, NEET and exam preparation tracker for syllabus and lecture tracking, revision, question practice, tests and performance analytics.",
              featureList: [
                "Syllabus and lecture tracking",
                "Spaced revision planner",
                "Question practice tracking",
                "Test tracker and performance analytics",
                "Daily study plan and streaks",
                "Study toolkit, simulations and virtual labs",
              ],
            },

            {
              "@type": "FAQPage",
              "@id": `${SITE_URL}/#faq`,
              mainEntity: homepageContent.faq.items.map((item) => ({
                "@type": "Question",
                name: item.question,
                acceptedAnswer: { "@type": "Answer", text: item.answer },
              })),
            },
            {
              "@type": "VideoObject",
              name: "How the study and preparation tracker works",
              description:
                "Walkthrough of setting up a study plan and tracking lectures, revision and tests in The Quantum Minds.",
              thumbnailUrl: "https://i.ytimg.com/vi/vPFK3GzxSOU/hqdefault.jpg",
              embedUrl: "https://www.youtube-nocookie.com/embed/vPFK3GzxSOU",
              contentUrl: "https://www.youtube.com/watch?v=vPFK3GzxSOU",
              uploadDate: "2026-08-27T08:00:30+00:00",
            },
          ],
        }),
      },
    ],
  }),

  component: DashboardPage,
});


const CARD =
  "relative overflow-hidden rounded-3xl border border-white/10 bg-card/70 p-5 shadow-[0_8px_30px_rgb(0,0,0,0.12)] backdrop-blur-xl";

function SectionHead({ title, action }: { title: string; action?: { label: string; to: string } }) {
  return (
    <div className="mb-3 flex items-baseline justify-between gap-3">
      <h2 className="text-[13px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
        {title}
      </h2>
      {action ? (
        <Link
          to={action.to}
          className="shrink-0 text-[12px] font-semibold text-[var(--primary-glow)] transition-all hover:translate-x-0.5 hover:opacity-80"
        >
          {action.label} →
        </Link>
      ) : null}
    </div>
  );
}

function Bar({
  percent,
  from = "from-primary",
  to = "to-primary-glow",
}: {
  percent: number;
  from?: string;
  to?: string;
}) {
  const value = Math.max(0, Math.min(100, percent));
  return (
    <div className="relative h-2 w-full overflow-hidden rounded-full bg-surface">
      <motion.div
        initial={{ width: 0 }}
        animate={{ width: `${value}%` }}
        transition={{ type: "spring", stiffness: 110, damping: 22 }}
        className={cn(
          "relative h-full rounded-full bg-gradient-to-r shadow-[0_0_14px_-2px_currentColor]",
          from,
          to,
        )}
      >
        <span className="absolute right-0 top-1/2 size-2.5 -translate-y-1/2 translate-x-1/3 rounded-full bg-white/90 shadow-[0_0_10px_2px_rgba(255,255,255,0.55)]" />
      </motion.div>
    </div>
  );
}

type Quick = { label: string; icon: LucideIcon; to: string; ring: string; tint: string };

const QUICK: Quick[] = [
  {
    label: "Revision",
    icon: CalendarCheck,
    to: "/revisions",
    ring: "ring-emerald-400/30",
    tint: "from-emerald-400/25 to-emerald-500/5 text-emerald-500 dark:text-emerald-300",
  },
  {
    label: "Questions",
    icon: ClipboardList,
    to: "/questions",
    ring: "ring-lime-400/30",
    tint: "from-lime-400/25 to-lime-500/5 text-lime-600 dark:text-lime-300",
  },
  {
    label: "Lectures",
    icon: GraduationCap,
    to: "/revisions",
    ring: "ring-emerald-400/30",
    tint: "from-emerald-400/25 to-emerald-600/5 text-emerald-600 dark:text-emerald-300",
  },
  {
    label: "Tests",
    icon: FlaskConical,
    to: "/tests",
    ring: "ring-orange-400/30",
    tint: "from-orange-400/25 to-orange-500/5 text-orange-500 dark:text-orange-300",
  },
  {
    label: "Toolkit",
    icon: Wrench,
    to: "/toolkit",
    ring: "ring-lime-500/30",
    tint: "from-lime-500/25 to-green-700/5 text-lime-700 dark:text-lime-400",
  },
];

function DashboardPage() {
  const { requirePremium } = usePremium();
  const { openUpgrade } = useUpgrade();
  const { entitlement } = useEntitlement();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const chaptersApi = useChapters();
  const { chapters, today, isPending } = chaptersApi;
  const [qError, setQError] = useState<string | null>(null);
  const [subOpen, setSubOpen] = useState(false);

  const { data: questionData } = useQuery({
    queryKey: ["question-chapters"],
    queryFn: () => fetchQuestionChapters(),
    staleTime: 5 * 60_000,
  });
  const questionChapters = useMemo(() => questionData ?? [], [questionData]);

  const solvePractice = useMutation({
    mutationFn: (vars: { taskId: string; solved: number }) =>
      completeQuestionPractice({ data: { taskId: vars.taskId, today, solved: vars.solved } }),
    retry: 2,
    onMutate: (vars) => {
      const stop = trackSave("dashboard-practice");
      setQError(null);
      const previous = queryClient.getQueryData<QuestionChapter[]>(["question-chapters"]);
      queryClient.setQueryData<QuestionChapter[]>(["question-chapters"], (old) =>
        (old ?? []).map((chapter) =>
          chapter.tasks.some((t) => t.id === vars.taskId)
            ? {
                ...chapter,
                solvedQuestions: chapter.solvedQuestions + vars.solved,
                tasks: chapter.tasks.map((t) =>
                  t.id === vars.taskId
                    ? {
                        ...t,
                        status: "completed" as const,
                        solvedQuestions: vars.solved,
                        completedAt: new Date().toISOString(),
                      }
                    : t,
                ),
              }
            : chapter,
        ),
      );
      notifySaved("Practice saved");
      return { previous, stop };
    },
    onError: (err: Error, _vars, ctx) => {
      ctx?.stop("error");
      if (ctx?.previous) queryClient.setQueryData(["question-chapters"], ctx.previous);
      setQError(err.message);
    },
    onSuccess: (_res, _vars, ctx) => ctx?.stop("ok"),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["question-chapters"] }),
  });

  const totalLectures = chapters.reduce((sum, c) => sum + c.totalLectures, 0);
  const doneLectures = chapters.reduce(
    (sum, c) => sum + c.lectures.filter((l) => l.completedOn).length,
    0,
  );
  const syllabusPct = totalLectures ? Math.round((doneLectures / totalLectures) * 100) : 0;

  const plan = useMemo(() => buildDailyPlan(chapters, today), [chapters, today]);
  const dueCount = plan.reduce((sum, g) => sum + g.revisions.length, 0);

  const todayPractice = useMemo(
    () =>
      questionChapters
        .map((chapter) => {
          const task = chapter.tasks.find((t) => taskState(t, today) === "today");
          if (!task) return null;
          return { chapter, task, target: effectiveTarget(chapter.tasks, task, today) };
        })
        .filter((v): v is NonNullable<typeof v> => v !== null),
    [questionChapters, today],
  );

  const questionsSolved = questionChapters.reduce((sum, c) => sum + c.solvedQuestions, 0);
  const questionsTargetTotal = questionChapters.reduce(
    (sum, c) => sum + c.tasks.reduce((s, t) => s + t.targetQuestions, 0),
    0,
  );
  const questionsPct = questionsTargetTotal
    ? Math.round((questionsSolved / questionsTargetTotal) * 100)
    : 0;

  const nextLecture = useMemo(() => {
    for (const chapter of chapters) {
      const pending = chapter.lectures
        .filter((l) => !l.completedOn)
        .sort((a, b) => a.number - b.number)[0];
      if (pending) {
        return {
          chapter,
          lecture: pending,
          done: chapter.lectures.filter((l) => l.completedOn).length,
        };
      }
    }
    return null;
  }, [chapters]);

  /** Same ordering the planner already uses: revisions, then practice, then lectures. */
  type Task = {
    key: string;
    subject: string;
    title: string;
    detail: string;
    percent: number;
    tone: string;
    from: string;
    to: string;
    onContinue: () => void;
  };

  const tasks: Task[] = [];
  for (const group of plan) {
    tasks.push({
      key: `rev-${group.chapterId}`,
      subject: group.subject,
      title: group.chapter,
      detail: `${group.revisions.length} ${group.revisions.length === 1 ? "revision" : "revisions"} due`,
      percent: 0,
      tone: "bg-emerald-500",
      from: "from-emerald-500",
      to: "to-teal-400",
      onContinue: () => navigate({ to: "/revisions" }),
    });
  }
  for (const { chapter, task, target } of todayPractice) {
    tasks.push({
      key: `q-${task.id}`,
      subject: chapter.subject,
      title: chapter.chapter,
      detail: `${task.solvedQuestions} of ${target} questions`,
      percent: target ? (task.solvedQuestions / target) * 100 : 0,
      tone: "bg-lime-500",
      from: "from-lime-500",
      to: "to-lime-300",
      onContinue: () =>
        requirePremium() && !solvePractice.isPending
          ? solvePractice.mutate({ taskId: task.id, solved: target })
          : undefined,
    });
  }
  if (nextLecture) {
    tasks.push({
      key: `l-${nextLecture.chapter.id}`,
      subject: nextLecture.chapter.subject,
      title: nextLecture.chapter.chapter,
      detail: `Lecture ${nextLecture.lecture.number} of ${nextLecture.chapter.totalLectures}`,
      percent: (nextLecture.done / nextLecture.chapter.totalLectures) * 100,
      tone: "bg-emerald-500",
      from: "from-emerald-600",
      to: "to-lime-400",
      onContinue: () => chaptersApi.openDetail(nextLecture.chapter.id),
    });
  }

  const topTask = tasks[0] ?? null;

  return (
    <main className="relative mx-auto min-h-screen w-full max-w-7xl px-4 pb-6 pt-7 sm:px-6 lg:px-8">
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10 opacity-70"
        style={{
          background:
            "radial-gradient(ellipse 70% 45% at 15% -5%, rgba(27,72,39,0.55), transparent 65%), radial-gradient(ellipse 60% 40% at 95% 5%, rgba(111,174,53,0.22), transparent 65%)",
        }}
      />
      <header className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3">
        <p className="min-w-0 truncate text-[15px]">
          <BrandWordmark />
        </p>
        <div className="flex shrink-0 items-center gap-2">
          <ShareButton />
          <ProfileHeaderButton />

        </div>
      </header>

      <div className="lg:mt-2 lg:grid lg:grid-cols-[minmax(0,7fr)_minmax(0,4fr)] lg:items-start lg:gap-8">
        <div className="min-w-0">
      {/* Today's Study — the single anchor of the page */}
      <section className="mt-8 lg:mt-6">

        <SectionHead title="Today's Study" />
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className={CARD}
        >
          <span
            aria-hidden
            className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/50 to-transparent"
          />
          {isPending ? (
            <div className="h-24 animate-pulse rounded-2xl bg-surface" />
          ) : topTask ? (
            <>
              <span className="inline-flex items-center rounded-full border border-lime-400/30 bg-lime-400/10 px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[0.14em] text-lime-700 shadow-[0_0_18px_-6px_rgba(130,195,65,0.9)] dark:text-lime-300">
                {topTask.subject}
              </span>
              <h3 className="mt-2.5 truncate text-2xl font-extrabold tracking-tight">
                {topTask.title}
              </h3>
              <p className="mt-1 text-[13px] text-muted-foreground">{topTask.detail}</p>
              <div className="mt-4">
                <Bar percent={topTask.percent} from={topTask.from} to={topTask.to} />
              </div>
              <button
                type="button"
                onClick={topTask.onContinue}
                className="group mt-5 flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[var(--primary)] to-[var(--primary-glow)] px-4 py-3.5 text-[14px] font-bold text-white shadow-[0_12px_30px_-10px_rgba(111,174,53,0.9)] ring-1 ring-inset ring-white/25 transition-all hover:brightness-110 active:scale-95"
              >
                Continue Learning
                <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
              </button>
              <Link
                to="/revisions"
                className="mt-3 block text-center text-[12px] font-medium text-muted-foreground transition-colors hover:text-[var(--primary-glow)]"
              >
                View today&apos;s plan →
              </Link>
            </>
          ) : (
            <>
              <h3 className="text-lg font-bold tracking-tight">Nothing scheduled today</h3>
              <p className="mt-1 text-[13px] text-muted-foreground">
                Add a chapter and your daily plan builds itself.
              </p>
              <button
                type="button"
                onClick={() => navigate({ to: "/revisions" })}
                className="group mt-5 flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[var(--primary)] to-[var(--primary-glow)] px-4 py-3.5 text-[14px] font-bold text-white shadow-[0_12px_30px_-10px_rgba(111,174,53,0.9)] ring-1 ring-inset ring-white/25 transition-all hover:brightness-110 active:scale-95"
              >
                Open planner
                <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
              </button>
            </>
          )}
        </motion.div>
      </section>

      {/* Subscription Campaign */}
      {!entitlement.paid_active ? (
        <section className="mt-6">
          <SubscriptionBanner onClick={() => setSubOpen(true)} />
        </section>
      ) : null}

      {/* Today's Plan — only when more than one task is pending */}
      {tasks.length > 1 ? (
        <section className="mt-9">
          <SectionHead
            title="Today's Plan"
            action={{ label: "View full plan", to: "/revisions" }}
          />
          <ul className="divide-y divide-white/10 overflow-hidden rounded-3xl border border-white/10 bg-card/60 shadow-[0_8px_30px_rgb(0,0,0,0.12)] backdrop-blur-xl">
            {tasks.slice(0, 3).map((task) => (
              <li key={task.key}>
                <button
                  type="button"
                  onClick={task.onContinue}
                  className="flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors hover:bg-white/5 active:scale-[0.99]"
                >
                  <span
                    className={cn(
                      "size-2 shrink-0 rounded-full shadow-[0_0_10px_2px_currentColor]",
                      task.tone,
                    )}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13.5px] font-semibold">
                      {task.subject} — {task.title}
                    </span>
                    <span className="mt-0.5 block truncate text-[11.5px] text-muted-foreground">
                      {task.detail}
                    </span>
                  </span>
                  <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
                </button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {subOpen ? (
        <Suspense fallback={null}>
          <SubscriptionModal open onClose={() => setSubOpen(false)} />
        </Suspense>
      ) : null}

      {/* Resources */}
      <section className="mt-9">
        <SectionHead title="Resources" />
        <div className="grid grid-cols-2 gap-3.5 md:grid-cols-3 lg:grid-cols-4">
          {[
            {
              to: "/simulations",
              icon: Atom,
              title: "Interactive Simulations",
              sub: "Play with physics in real time",
              tint: "text-lime-600 dark:text-lime-300",
              access: "PRO" as const,
            },
            {
              to: "/toolkit",
              icon: Wrench,
              title: "JEE Toolkit",
              sub: "Formulas, tables & calculators",
              tint: "text-emerald-600 dark:text-emerald-300",
              access: "FREE" as const,
            },
            {
              to: "/what-if",
              icon: FlaskConical,
              title: "What If? Lab",
              sub: "Change one rule. Watch reality react.",
              tint: "text-orange-500 dark:text-orange-300",
              access: "PRO" as const,
            },
            {
              to: "/pyq-predictor",
              icon: TrendingUp,
              title: "PYQ Predictor",
              sub: "Chapter-wise JEE & NEET trends",
              tint: "text-emerald-500 dark:text-emerald-300",
              access: "PRO + FREE" as const,
            },
            {
              to: "/prerequisites",
              icon: Layers,
              title: "Chapter Prerequisites",
              sub: "Before you start: basics & mistakes",
              tint: "text-green-600 dark:text-green-300",
              access: "PRO + FREE" as const,
            },
            {
              to: "/mindmaps",
              icon: Network,
              title: "Mindmaps",
              sub: "Chapter mindmaps for JEE & NEET",
              tint: "text-lime-700 dark:text-lime-400",
              access: "PRO" as const,
            },
            {
              to: "/college-predictor",
              icon: GraduationCap,
              title: "College Predictor",
              sub: "Rank vs cutoffs for JEE & NEET",
              tint: "text-emerald-700 dark:text-emerald-400",
              access: "PRO" as const,
            },
            {
              to: "/community",
              icon: Users,
              title: "Our Community",
              sub: "About us, channels & support",
              tint: "text-rose-500 dark:text-rose-300",
              access: "FREE" as const,
            },
          ].map((r) => (
            <Link
              key={r.to}
              to={r.to}
              className="group rounded-3xl border border-white/10 bg-card/60 p-4 shadow-[0_8px_30px_rgb(0,0,0,0.12)] backdrop-blur-xl transition-all hover:-translate-y-0.5 hover:border-[var(--primary-glow)]/40 active:scale-95"
            >
              <r.icon className={cn("size-5 transition-transform group-hover:scale-110", r.tint)} />
              <p className="mt-2 flex items-center gap-1.5 text-[13px] font-semibold leading-tight">
                {r.title}
                {r.access === "PRO" ? (
                  <Crown className="size-3.5 text-amber-400" />
                ) : null}
              </p>
              <p className="mt-0.5 text-[11px] leading-tight text-muted-foreground">{r.sub}</p>
            </Link>
          ))}

        </div>
      </section>

      <FeatureShowcaseBanner
        isUserSubscribed={entitlement.paid_active}
        onNavigate={(route) => navigate({ to: route })}
        onOpenUpgradeModal={() => openUpgrade()}
      />
        </div>

        {/* Desktop side column */}
        <div className="min-w-0 lg:sticky lg:top-6">
      {/* Quick Access */}
      <section className="mt-9 lg:mt-0">

        <SectionHead title="Quick Access" />
        <div className="grid grid-cols-5 gap-2">
          {QUICK.map((q, i) => (
            <motion.div
              key={q.label}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.04 * i, duration: 0.3 }}
            >
              <Link
                to={q.to}
                className="flex flex-col items-center gap-2 rounded-2xl border border-white/10 bg-card/60 px-1 py-3 text-center shadow-[0_8px_24px_-14px_rgba(0,0,0,0.6)] backdrop-blur-xl transition-transform hover:-translate-y-0.5 active:scale-95"
              >
                <span
                  className={cn(
                    "grid size-10 place-items-center rounded-full bg-gradient-to-br ring-1",
                    q.tint,
                    q.ring,
                  )}
                >
                  <q.icon className="size-4.5" />
                </span>
                <span className="text-[10.5px] font-semibold leading-tight">{q.label}</span>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Your Progress */}
      <section className="mt-9">
        <SectionHead title="Your Progress" />
        <div className={CARD}>
          {[
            {
              label: "Syllabus completion",
              value: `${syllabusPct}%`,
              percent: syllabusPct,
              from: "from-emerald-600",
              to: "to-lime-400",
            },
            {
              label: "Questions solved",
              value: `${questionsSolved}`,
              percent: questionsPct,
              from: "from-lime-500",
              to: "to-lime-300",
            },
            {
              label: "Lectures completed",
              value: `${doneLectures} / ${totalLectures}`,
              percent: syllabusPct,
              from: "from-[var(--surface)]",
              to: "to-[var(--primary-glow)]",
            },
          ].map((row, i) => (
            <div key={row.label} className={i > 0 ? "mt-4" : undefined}>
              <div className="mb-1.5 flex items-baseline justify-between text-[12.5px]">
                <span className="text-muted-foreground">{row.label}</span>
                <span className="font-bold tabular-nums">{row.value}</span>
              </div>
              <Bar percent={row.percent} from={row.from} to={row.to} />
            </div>
          ))}
          <Link
            to="/tests/performance"
            className="mt-4 inline-block text-[12px] font-semibold text-[var(--primary-glow)] transition-transform hover:translate-x-0.5"
          >
            View detailed progress →
          </Link>
        </div>
      </section>
        </div>
      </div>

      <QuotaNotice message={chaptersApi.error ?? qError} className="mt-6" />


      <ChapterDetailSheet
        chapter={chaptersApi.detail}
        today={today}
        busy={chaptersApi.busy}
        onClose={chaptersApi.closeDetail}
        onMarkLecture={chaptersApi.markLecture}
        onCompleteRevisions={chaptersApi.completeRevisions}
        onUpdateLectures={(total) =>
          chaptersApi.detail ? chaptersApi.setLectureCount(chaptersApi.detail.id, total) : undefined
        }
        onDelete={() =>
          chaptersApi.detail ? chaptersApi.deleteChapter(chaptersApi.detail.id) : undefined
        }
      />
    </main>
  );
}
